import { api } from "../index";

export interface BackendNotification {
  notification_id: number;
  event_type: string;
  event_subtype?: string;
  category: string;
  severity: string;
  priority: string;
  recipient_id: string;
  recipient_email?: string;
  title: string;
  message: string;
  action_url?: string;
  icon?: string;
  status: string;
  requires_action: boolean;
  created_date: string;
  read_date?: string;
  acknowledged_date?: string;
  expires_at?: string;
}

export interface NotificationFilters {
  status?: string;
  event_type?: string;
  category?: string;
}

export interface NotificationPagination {
  page?: number;
  limit?: number;
}

export interface NotificationResponse {
  notifications: BackendNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  unread_count: number;
}

/**
 * Get user notifications
 */
export const getUserNotifications = async (
  filters?: NotificationFilters,
  pagination?: NotificationPagination
): Promise<NotificationResponse> => {
  const params = new URLSearchParams();
  
  // Get recipient_id from sessionStorage (fallback if backend middleware doesn't extract it)
  const recipientId = sessionStorage.getItem("user_id");
  if (recipientId) {
    params.append('recipient_id', recipientId);
  }
  
  if (filters?.status) params.append('status', filters.status);
  if (filters?.event_type) params.append('event_type', filters.event_type);
  if (filters?.category) params.append('category', filters.category);
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());

  const res = await api.get(`/api/notifications?${params.toString()}`);
  return res.data;
};

/**
 * Get notification by ID
 */
export const getNotificationById = async (notificationId: number): Promise<BackendNotification> => {
  const res = await api.get(`/api/notifications/${notificationId}`);
  return res.data;
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (notificationId: number): Promise<void> => {
  const recipientId = sessionStorage.getItem("user_id");
  const body = recipientId ? { recipient_id: recipientId } : {};
  await api.put(`/api/notifications/${notificationId}/read`, body);
};

/**
 * Mark notification as acknowledged
 */
export const markNotificationAsAcknowledged = async (notificationId: number): Promise<void> => {
  const recipientId = sessionStorage.getItem("user_id");
  const body = recipientId ? { recipient_id: recipientId } : {};
  await api.put(`/api/notifications/${notificationId}/acknowledge`, body);
};

/**
 * Get unread count
 */
export const getUnreadCount = async (): Promise<number> => {
  const params = new URLSearchParams();
  const recipientId = sessionStorage.getItem("user_id");
  if (recipientId) {
    params.append('recipient_id', recipientId);
  }
  
  const res = await api.get(`/api/notifications/unread/count?${params.toString()}`);
  return res.data.unread_count;
};

/**
 * Get notification statistics
 */
export const getNotificationStats = async (): Promise<any> => {
  const params = new URLSearchParams();
  const recipientId = sessionStorage.getItem("user_id");
  if (recipientId) {
    params.append('recipient_id', recipientId);
  }
  
  const res = await api.get(`/api/notifications/stats/summary?${params.toString()}`);
  return res.data;
};

